import { useEffect, useState } from "react";
import { CaptureFlow, Capture } from "./Capture";
import { Incident } from "./Incident";
import { loadState, submitTarget, State, Target } from "./api";

type Props = { otp: string };

export function Session({ otp }: Props) {
  const [state, setState] = useState<State | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);
  // Set true when the user clicks "Check out & then check in" on a stale
  // open-shift target. CaptureFlow carries it through to the backend.
  const [recoveryTargetId, setRecoveryTargetId] = useState<string | null>(null);

  useEffect(() => {
    loadState(otp).then(setState).catch((e) => setError(String(e.message || e)));
  }, [otp]);

  if (error) {
    return (
      <Shell>
        <h2>Link not usable</h2>
        <p style={{ color: "#b00" }}>{error}</p>
      </Shell>
    );
  }
  if (!state) {
    return <Shell><p>Loading…</p></Shell>;
  }
  if (state.kind === "incident") {
    return <Incident otp={otp} />;
  }

  const kind = state.kind as "checkin" | "checkout";

  // A target is "mismatched" if the preflight conflicts with the session kind.
  // Mismatched targets with recovery enabled are still actionable.
  const mismatched = (t: Target) =>
    (state.kind === "checkin" && t.already_checked_in) ||
    (state.kind === "checkout" && t.not_checked_in);

  const isRecovery = (t: Target) => t.id === recoveryTargetId;
  const skipped = (t: Target) => mismatched(t) && !isRecovery(t);

  const actionable = state.targets.filter((t) => t.status === "pending" && !skipped(t));
  const resolved = state.targets.filter((t) => t.status !== "pending" || skipped(t));

  // Single-target self-checkin with a preflight mismatch: show the
  // corrective screen with a recovery option.
  if (state.targets.length === 1 && mismatched(state.targets[0]) && !isRecovery(state.targets[0])) {
    return (
      <PreflightMismatch
        state={state}
        target={state.targets[0]}
        onRecover={() => setRecoveryTargetId(state.targets[0].id)}
      />
    );
  }

  if (actionable.length === 0) {
    return (
      <Shell>
        <h2>All done</h2>
        <BatchReceipt targets={state.targets} kind={state.kind} />
      </Shell>
    );
  }
  const current = actionable[Math.min(cursor, actionable.length - 1)];

  const handle = async (c: Capture) => {
    const res = await submitTarget(otp, current.id, c);
    const updated = { ...current, status: "submitted" as const, guardian_ref: res.guardian_ref ?? null };
    setState({
      ...state,
      targets: state.targets.map((t) => (t.id === current.id ? updated : t)),
    });
    setCursor((c) => c + 1);
    if (recoveryTargetId === current.id) setRecoveryTargetId(null);
  };

  return (
    <Shell>
      <h2>{labelFor(state.kind)} — {actionable.length} remaining</h2>
      {resolved.length > 0 && <BatchReceipt targets={resolved} kind={state.kind} compact />}
      <div style={{ marginTop: "1.5rem" }}>
        <CaptureFlow
          key={current.id + (isRecovery(current) ? "-recovery" : "")}
          guardName={current.guard_name}
          kind={kind}
          postOrders={current.post_orders}
          previousHandover={current.previous_handover}
          forceCheckoutThenCheckin={isRecovery(current)}
          onCapture={handle}
        />
      </div>
    </Shell>
  );
}

function PreflightMismatch({ state, target, onRecover }: { state: State; target: Target; onRecover: () => void }) {
  if (state.kind === "checkin" && target.already_checked_in) {
    return (
      <Shell>
        <h2>Already checked in</h2>
        <p>
          Guardian already has an open shift for <strong>{target.guard_name}</strong>
          {target.checked_in_at && (
            <> since <strong>{new Date(target.checked_in_at).toLocaleString()}</strong></>
          )}.
        </p>
        <p>If you forgot to check out at the end of your last shift, you can close it and start a new one in one go:</p>
        <button
          onClick={onRecover}
          style={{
            padding: "0.9rem 1.2rem", fontSize: "1.05rem", background: "#b45309", color: "white",
            border: "none", borderRadius: 8, cursor: "pointer", marginTop: "0.5rem",
          }}
        >
          Check out & then check in
        </button>
        <p style={{ color: "#777", marginTop: "1rem", fontSize: "0.9rem" }}>
          Otherwise, text <em>"Check out"</em> to the bot on WhatsApp to end the current shift normally.
        </p>
      </Shell>
    );
  }
  if (state.kind === "checkout" && target.not_checked_in) {
    return (
      <Shell>
        <h2>Not checked in</h2>
        <p>
          Guardian has no open shift for <strong>{target.guard_name}</strong> right now.
        </p>
        <p>To start a shift, text <em>"Check in"</em> to the bot on WhatsApp.</p>
      </Shell>
    );
  }
  return <Shell><p>Nothing to do.</p></Shell>;
}

function BatchReceipt({ targets, kind, compact = false }: { targets: Target[]; kind: State["kind"]; compact?: boolean }) {
  const label = (t: Target): { text: string; color: string } => {
    if (kind === "checkin" && t.already_checked_in) return { text: "already checked in — skipped", color: "#b85900" };
    if (kind === "checkout" && t.not_checked_in) return { text: "not on shift — skipped", color: "#b85900" };
    return { text: t.status, color: statusColor(t.status) };
  };
  return (
    <div>
      {!compact && <p>Receipt:</p>}
      <ul style={{ paddingLeft: "1.1rem" }}>
        {targets.map((t) => {
          const { text, color } = label(t);
          return (
            <li key={t.id} style={{ color }}>
              {t.guard_name} — <strong>{text}</strong>
              {t.guardian_ref && <span style={{ color: "#777" }}> (#{t.guardian_ref})</span>}
              {t.failure_reason && <span style={{ color: "#b00" }}> — {t.failure_reason}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main style={{ fontFamily: "system-ui", maxWidth: 560, margin: "2rem auto", padding: "0 1rem" }}>
      {children}
    </main>
  );
}

function labelFor(kind: State["kind"]): string {
  return { checkin: "Check in", checkout: "Check out", incident: "Incident" }[kind];
}

function statusColor(s: Target["status"]) {
  return { pending: "#555", submitted: "#2a7a2a", failed: "#b00000" }[s];
}
